/* eslint-disable no-prototype-builtins */
import DateTimeFormat from '@stubhub/dateformat-utils';
import {Storage} from '@stubhub/legacy-cache';
import {cookies} from '@stubhub/react-cookies';

import {PAYOUT_HISTORY_FILTER_PERIODS as DATEFILTER} from '../shared/constants';

/**
 * =============== Device related helper functions ===============
 */
const getDeviceType = () => {
  var userAgent = navigator.userAgent.toLowerCase();
  if (/iphone/i.test(userAgent) || /ipad/i.test(userAgent)) {
    if (/version/i.test(userAgent) && /safari/i.test(userAgent)) {
      return 'ios_safari';
    }
    return 'ios_other';
  } else if (/android/i.test(userAgent)) {
    if (/firefox/i.test(userAgent)) {
      return 'android_firefox';
    }
    return 'android_common';
  }
  return 'other';
};

function isDesktop() {
  return !(
    /Android/i.test(navigator.userAgent) ||
    /iPhone|iPad|iPod/i.test(navigator.platform) ||
    /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    /IEMobile/i.test(navigator.userAgent)
  );
}

function isMobileView(storeState) {
  return storeState?.viewport?.screenSize === 'small';
}

/**
 * =============== Browser related helper functions ===============
 */

function getUrlParameter(parameterName) {
  let parameterKeyValueArray = getUrlParameterKeyValueArray();
  return parameterKeyValueArray[parameterName];
}

function getUrlParameterKeyValueArray() {
  const parameterString = window.location.search.substring(1);
  let parametersArray = parameterString.split('&');
  let keyValuePairs = [];

  for (let parameter in parametersArray) {
    let keyValueSplit = parametersArray[parameter].split('=');
    keyValuePairs[keyValueSplit[0]] = keyValueSplit[1];
  }

  return keyValuePairs;
}

const cancelBubble = (e) => {
  var ev = e || window.event;
  if (ev && ev.stopPropagation) {
    ev.stopPropagation();
  } else {
    ev.cancelBubble = true;
  }
};

const setValueInSessionStorage = (key, value) => {
  Storage.setItem(`MYA:${key}`, value, 'session');
};

const getValueFromSessionStorage = (key) => {
  return Storage.getItem(`MYA:${key}`, 'session');
};

/**
 * =============== Date Helper Function ===============
 */

// input should be like: 2020-09-01T23:53:39-zone
const getDateFromUTCString = (str) => {
  const arr = str.split('T')[0].split('-');
  return new Date(arr[0], parseInt(arr[1], 10) - 1, arr[2]);
};

/**
 * @param {*} time - Date in millisecond
 * @returns Date in format: YYYY-MM-dd
 */
const getDateFromTime = (time) => {
  return DateTimeFormat.toAPIString(new Date(time), '');
};

// output format will be: hh:mm:ss.sss
const getUTCTimeString = (date) => {
  const hour = padZeros(`${date.getUTCHours()}`, 2);
  const min = padZeros(`${date.getUTCMinutes()}`, 2);
  const sec = padZeros(`${date.getUTCSeconds()}`, 2);
  const msec = padZeros(`${date.getUTCMilliseconds()}`, 3);
  return `${hour}:${min}:${sec}.${msec}`;
};

// output format will be: yyyy-mm-dd
const getUTCDateString = (date) => {
  const year = padZeros(`${date.getUTCFullYear()}`, 2);
  const month = padZeros(`${date.getUTCMonth() + 1}`, 2);
  const day = padZeros(`${date.getUTCDate()}`, 2);
  return [year, month, day].join('-');
};

// output format will be: yyyy-mm-ddThh:mm:ss.sssZ
const getUTCDateFullString = (date) => {
  const datePart = getUTCDateString(date);
  const timePart = getUTCTimeString(date);
  return `${datePart}T${timePart}Z`;
};

/**
 * @param {*} date in format: yyyy-mm-ddThh:mm:ss-0000
 * @returns [yyyy-mm-dd, hh:mm:ss, -0000]
 */
const formatDateToParts = (fulldate) => {
  const [date, tnz] = fulldate.split('T');
  const zoneIndex = tnz.indexOf('-') > 0 ? tnz.indexOf('-') : tnz.indexOf('+');
  const time = tnz.slice(0, zoneIndex);
  const zone = tnz.slice(zoneIndex, tnz.length);
  return [date, time, zone];
};

/**
 * @param {*} filter as string
 * @returns uri encoded string in format: "KEY:DATEFROM TO DATETO"
 */
const getFilterDateFromFilterKey = (filter, key) => {
  const dateFrom = new Date(Date.now());
  const dateTo = new Date(Date.now());
  let dateFromString;
  let dateToString = getUTCDateString(dateTo);
  switch (filter) {
    case DATEFILTER.LAST_7:
      dateFrom.setDate(dateTo.getDate() - 7);
      dateFromString = getUTCDateString(dateFrom);
      break;
    case DATEFILTER.LAST_30:
      dateFrom.setDate(dateTo.getDate() - 30);
      dateFromString = getUTCDateString(dateFrom);
      break;
    case DATEFILTER.LAST_90:
      dateFrom.setDate(dateTo.getDate() - 90);
      dateFromString = getUTCDateString(dateFrom);
      break;
    case DATEFILTER.THIS_YEAR:
      dateFromString = `${dateTo.getFullYear()}-01-01`;
      break;
    case DATEFILTER.PAST_YEAR:
      dateFromString = `${dateTo.getFullYear() - 1}-01-01`;
      dateToString = `${dateTo.getFullYear() - 1}-12-31`;
      break;
    default:
      return false;
  }
  return `${key}:${dateFromString} TO ${dateToString}`;
};

/**
 * =============== Format related Helper Functions ===============
 */

const getFormattedPriceByCurrency = ({amount, currency}, locale) => {
  return new Intl.NumberFormat(locale || [], {
    style: 'currency',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    currency: currency,
  }).format(amount);
};

const formatPrice = (price) => {
  return (
    price &&
    price
      .replace(/[^\d.-]/g, '') // only accept number/point/minus sign
      .replace(/^-/, '###')
      .replace(/-/g, '')
      .replace('###', '-') // only one minus sign is allowed at the beginning
      .replace(/^\./g, '') // the decimal point is not allowed at the beginning
      .replace(/^(-)\./, '$1') // the decimal point is not allowed after minus
      .replace(/^(-?\d{7})\d+/, '$1') // maximum input million
      .replace(/\.{2,}/g, '.') // only two decimal places are allowed
      .replace('.', '$#$')
      .replace(/\./g, '')
      .replace('$#$', '.') // only one point allowed
      .replace(/^(-?)(\d{1,7})\.(\d\d).*$/, '$1$2.$3')
  ); // allowed million number with two decimal and minus sign
};

const formatPhoneNumber = (phoneNumber, phoneCallingCode) => {
  phoneNumber = phoneNumber + '';
  var separator = ' ';
  var phoneNumberFormat = phoneNumber
    .replace(/[^\d]/g, '')
    .replace(/(\d{3})(\d{3})(\d{4})/, '$1' + separator + '$2' + separator + '$3');

  return phoneCallingCode ? '+' + phoneCallingCode + separator + phoneNumberFormat : phoneNumberFormat;
};

/**
 * =============== Business Logic Helper Functions ===============
 */

const isFeatureEnabled = (key, gsConfig) => {
  if (gsConfig && gsConfig.features && gsConfig.features[key]) {
    return !!gsConfig.features[key];
  }
  return false;
};

const getUserGUID = () => {
  if (!__CLIENT__ || !document || !document.cookie) {
    return '';
  }
  return cookies.get('session_userGUID') || '';
};

const getCSRFtoken = () => {
  if (!__CLIENT__ || !document || !document.cookie) {
    return '';
  }
  return cookies.get('session_CSRFtoken') || '';
};

const getLangPath = (currentLocale, defaultLocale) => {
  if (!currentLocale || !defaultLocale) {
    return '';
  }
  const lang = currentLocale !== defaultLocale ? currentLocale.substr(0, 2) : '';
  return lang ? `/${lang}` : '';
};

const createLatencyData = (name, id) => {
  const key = `MYA_LATENCY_DATA_${name}`;
  const expireTime = 2 * 60 * 1000; // expire the id in cache after 2min in ms.
  const action = 'update';
  const latencyDataFromStorage = sessionStorage.getItem(key) ? JSON.parse(sessionStorage.getItem(key)) : {};

  const latencyIds = latencyDataFromStorage.ids || [];

  // if passed id is an array
  if (Array.isArray(id)) {
    id.forEach((i) => {
      if (latencyIds.indexOf(i) === -1) {
        latencyIds.push(i);
      }
    });
  } else {
    if (latencyIds.indexOf(id) === -1) {
      latencyIds.push(id);
    }
  }

  const ob = {
    action: action,
    ids: latencyIds,
  };

  sessionStorage.setItem(key, JSON.stringify(ob), expireTime);
};

const getLinkForMap = (addr) => {
  addr = encodeURIComponent(addr);
  var device = getDeviceType();
  var link = {
    target: '_blank',
  };
  switch (device) {
    case 'ios_safari':
      link.url = 'http://maps.apple.com/?q=' + addr;
      break;
    case 'ios_other':
      link.url = 'http://maps.google.com/?q=' + addr;
      break;
    case 'android_firefox':
      link.url = 'geo:0,0?q=' + addr;
      link.target = '';
      break;
    case 'android_common':
      link.url = 'geo:0,0?q=' + addr;
      break;
    default:
      link.url = 'http://maps.google.com/?q=' + addr;
  }
  return link;
};

const getEventCountryFeatureByName = (features, eventCountry, featureName) => {
  const eventCountryFeatures = features?.countries?.find((feature) => feature.countryCode === eventCountry);
  return getFeatureByName(eventCountryFeatures?.features, featureName);
};

const getFeatureByName = (features, featureName) => {
  const countryFeature = features?.find((feature) => feature.name === featureName);
  return countryFeature?.value || false;
};

/**
 * =============== Common helper function ===============
 */

function isEmpty(obj) {
  for (var key in obj) {
    if (obj.hasOwnProperty(key)) {
      return false;
    }
  }
  return true;
}

/**
 * invoke func in a limitted rate
 * @param {*} func function to be invoked
 * @param {*} wait time to wait
 */
const debounce = (func, wait = 500) => {
  let timer = null;
  return function (...params) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      func.call(this, ...params);
    }, wait);
  };
};

const padZeros = (str, desiredLength) => {
  let existingLength = str.length;
  let pad = '';

  if (desiredLength > existingLength) {
    for (let start = existingLength; start < desiredLength; start++) {
      pad += '0';
    }
  }

  return `${pad}${str}`;
};

function image2Base64(img) {
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, img.width, img.height);
  const dataURL = canvas.toDataURL('image/png');
  return dataURL;
}

export {
  // Device related helper functions
  getDeviceType,
  isDesktop,
  isMobileView,

  // Browser related helper functions
  cancelBubble,
  getUrlParameter,
  getValueFromSessionStorage,
  setValueInSessionStorage,

  // Date Helper Function
  getDateFromUTCString,
  getDateFromTime,
  getUTCDateString,
  getUTCDateFullString,
  getUTCTimeString,
  formatDateToParts,
  getFilterDateFromFilterKey,

  // Format related Helper Functions
  formatPrice,
  formatPhoneNumber,
  getFormattedPriceByCurrency,

  // Business Logic Helper Functions
  createLatencyData,
  isFeatureEnabled,
  getLinkForMap,
  getUserGUID,
  getCSRFtoken,
  getLangPath,
  getFeatureByName,
  getEventCountryFeatureByName,

  // Common helper function
  debounce,
  isEmpty,
  image2Base64,
  padZeros,
};
